/**
 * @file 节点交互控制器 - 处理文字区域检测、光标变化、连线/拖动模式
 * 新交互模式：
 * - 点击节点文字区域：选中节点，显示8个缩放控制点和操作菜单
 * - 点击节点非文字区域：进入连线模式
 * - 鼠标移入节点：显示十字光标（表示可连线）
 * - 鼠标移入文字区域：显示移动光标（表示可拖动）
 */

import { Core, NodeSingular, Position } from 'cytoscape';

// 交互状态枚举
export enum InteractionMode {
  None = 'none',
  Selected = 'selected', // 选中状态（点击文字区域）
  Connecting = 'connecting', // 连线模式
  Resizing = 'resizing', // 调整大小模式
  Dragging = 'dragging', // 拖动模式
  BoxSelecting = 'box-selecting', // 框选模式
}

// 缩放控制点位置
export type ResizeHandle =
  | 'nw'
  | 'n'
  | 'ne' // 上排：左上、上中、右上
  | 'w'
  | 'e' // 中排：左中、右中
  | 'sw'
  | 's'
  | 'se'; // 下排：左下、下中、右下

// 交互状态接口
export interface InteractionState {
  mode: InteractionMode;
  selectedNode: NodeSingular | null;
  selectedNodes: NodeSingular[]; // 多选节点（框选）
  sourceNode: NodeSingular | null; // 连线源节点
  targetNode: NodeSingular | null; // 连线目标节点
  activeHandle: ResizeHandle | null; // 当前激活的缩放控制点
  isLabelHovered: boolean; // 鼠标是否在文字区域
  boxSelectStart: Position | null; // 框选起始位置
  boxSelectEnd: Position | null; // 框选结束位置
}

// 创建初始状态
export const createInitialState = (): InteractionState => ({
  mode: InteractionMode.None,
  selectedNode: null,
  selectedNodes: [],
  sourceNode: null,
  targetNode: null,
  activeHandle: null,
  isLabelHovered: false,
  boxSelectStart: null,
  boxSelectEnd: null,
});

// 文字区域边界框缓存
const labelBBoxCache = new Map<
  string,
  {
    x: number;
    y: number;
    width: number;
    height: number;
    timestamp: number;
  }
>();

// 缓存过期时间（毫秒）
const CACHE_EXPIRE_TIME = 500;

/**
 * 获取节点文字区域的边界框
 * 使用缓存避免频繁计算
 */
export const getNodeLabelBBox = (
  node: NodeSingular
): {
  x: number;
  y: number;
  width: number;
  height: number;
  timestamp: number;
} => {
  const nodeId = node.id();
  const now = Date.now();
  const cached = labelBBoxCache.get(nodeId);

  // 检查缓存是否有效
  if (cached && now - cached.timestamp < CACHE_EXPIRE_TIME) {
    return cached;
  }

  // 获取节点位置和尺寸
  const pos = node.position();
  const nodeWidth = node.width();
  const nodeHeight = node.height();
  const bb = node.boundingBox({ includeLabels: false });

  // 获取节点的文字内容
  const label = node.data('MMRef')?.label || node.data('label') || '';

  // 估算文字尺寸（简化版本，实际可以更精确）
  const fontSize = parseFloat(node.style('font-size')) || 14;
  const labelWidth = Math.min(label.length * fontSize * 0.6, nodeWidth * 0.9);
  const labelHeight = fontSize * 1.5;

  // 检查是否是父节点
  const isParent = node.isParent();

  let bbox;
  if (isParent) {
    // 父节点：文字虽然显示在边界外上方(text-valign: top)，
    // 但由于tapstart事件只在节点边界内触发，
    // 所以可拖动区域设为节点内部顶部区域
    const topDragHeight = 30; // 顶部30px为可拖动区域
    bbox = {
      x: pos.x - labelWidth / 2,
      y: bb.y1, // 从节点顶部边界开始（边界内）
      width: labelWidth,
      height: topDragHeight,
      timestamp: now,
    };
  } else {
    // 普通节点：文字居中
    bbox = {
      x: pos.x - labelWidth / 2,
      y: pos.y - labelHeight / 2,
      width: labelWidth,
      height: labelHeight,
      timestamp: now,
    };
  }

  // 更新缓存
  labelBBoxCache.set(nodeId, bbox);

  return bbox;
};

/**
 * 检查点是否在文字区域内
 */
export const isPointInLabelArea = (
  node: NodeSingular,
  point: Position
): boolean => {
  const bbox = getNodeLabelBBox(node);
  return (
    point.x >= bbox.x &&
    point.x <= bbox.x + bbox.width &&
    point.y >= bbox.y &&
    point.y <= bbox.y + bbox.height
  );
};

/**
 * 清除节点文字边界框缓存
 * 当节点尺寸/位置变化时调用
 */
export const clearLabelBBoxCache = (nodeId?: string) => {
  if (nodeId) {
    labelBBoxCache.delete(nodeId);
  } else {
    labelBBoxCache.clear();
  }
};

/**
 * 获取缩放控制点的位置
 */
export const getResizeHandlePositions = (
  node: NodeSingular
): Record<ResizeHandle, Position> => {
  const bb = node.boundingBox({ includeLabels: false });
  const x1 = bb.x1;
  const y1 = bb.y1;
  const x2 = bb.x2;
  const y2 = bb.y2;
  const cx = (x1 + x2) / 2;
  const cy = (y1 + y2) / 2;

  return {
    nw: { x: x1, y: y1 },
    n: { x: cx, y: y1 },
    ne: { x: x2, y: y1 },
    w: { x: x1, y: cy },
    e: { x: x2, y: cy },
    sw: { x: x1, y: y2 },
    s: { x: cx, y: y2 },
    se: { x: x2, y: y2 },
  };
};

/**
 * 检查点是否在某个缩放控制点上
 */
export const getHitResizeHandle = (
  node: NodeSingular,
  point: Position,
  handleSize: number = 8
): ResizeHandle | null => {
  const handles = getResizeHandlePositions(node);
  const halfSize = handleSize / 2;

  for (const [handle, pos] of Object.entries(handles)) {
    if (
      point.x >= pos.x - halfSize &&
      point.x <= pos.x + halfSize &&
      point.y >= pos.y - halfSize &&
      point.y <= pos.y + halfSize
    ) {
      return handle as ResizeHandle;
    }
  }

  return null;
};

/**
 * 根据缩放控制点获取对应的光标样式
 */
export const getResizeCursor = (handle: ResizeHandle): string => {
  const cursorMap: Record<ResizeHandle, string> = {
    nw: 'nwse-resize',
    n: 'ns-resize',
    ne: 'nesw-resize',
    w: 'ew-resize',
    e: 'ew-resize',
    sw: 'nesw-resize',
    s: 'ns-resize',
    se: 'nwse-resize',
  };
  return cursorMap[handle];
};

/**
 * 计算调整大小后的节点尺寸
 */
export const calculateResizedDimensions = (
  node: NodeSingular,
  handle: ResizeHandle,
  startPos: Position,
  currentPos: Position,
  minWidth: number = 40,
  minHeight: number = 30
): { width: number; height: number; x: number; y: number } | null => {
  const bb = node.boundingBox({ includeLabels: false });
  const dx = currentPos.x - startPos.x;
  const dy = currentPos.y - startPos.y;

  let newWidth = bb.w;
  let newHeight = bb.h;
  let newX = node.position('x');
  let newY = node.position('y');

  // 根据不同的控制点调整尺寸
  switch (handle) {
    case 'nw':
      newWidth = Math.max(minWidth, bb.w - dx);
      newHeight = Math.max(minHeight, bb.h - dy);
      newX = bb.x2 - newWidth / 2;
      newY = bb.y2 - newHeight / 2;
      break;
    case 'n':
      newHeight = Math.max(minHeight, bb.h - dy);
      newY = bb.y2 - newHeight / 2;
      break;
    case 'ne':
      newWidth = Math.max(minWidth, bb.w + dx);
      newHeight = Math.max(minHeight, bb.h - dy);
      newX = bb.x1 + newWidth / 2;
      newY = bb.y2 - newHeight / 2;
      break;
    case 'w':
      newWidth = Math.max(minWidth, bb.w - dx);
      newX = bb.x2 - newWidth / 2;
      break;
    case 'e':
      newWidth = Math.max(minWidth, bb.w + dx);
      newX = bb.x1 + newWidth / 2;
      break;
    case 'sw':
      newWidth = Math.max(minWidth, bb.w - dx);
      newHeight = Math.max(minHeight, bb.h + dy);
      newX = bb.x2 - newWidth / 2;
      newY = bb.y1 + newHeight / 2;
      break;
    case 's':
      newHeight = Math.max(minHeight, bb.h + dy);
      newY = bb.y1 + newHeight / 2;
      break;
    case 'se':
      newWidth = Math.max(minWidth, bb.w + dx);
      newHeight = Math.max(minHeight, bb.h + dy);
      newX = bb.x1 + newWidth / 2;
      newY = bb.y1 + newHeight / 2;
      break;
  }

  return { width: newWidth, height: newHeight, x: newX, y: newY };
};

/**
 * 获取节点文字的最小边界框尺寸
 * 用于限制节点缩放的最小尺寸
 * 复用 computeAutoLabelWidth 的计算逻辑，确保与添加节点时的宽度一致
 */
export const getMinNodeSize = (
  node: NodeSingular
): { minWidth: number; minHeight: number } => {
  // 直接使用节点当前的 labelWidth，这是添加节点时计算的宽度
  // 如果没有 labelWidth，使用节点当前宽度
  const labelWidth = node.data('labelWidth');
  const currentWidth = node.width();
  const minWidth = labelWidth || currentWidth || 60;

  const fontSize = parseFloat(node.style('font-size')) || 14;
  const textHeight = fontSize * 1.5;
  const minHeight = Math.max(30, textHeight + 20);

  return {
    minWidth: minWidth,
    minHeight: minHeight,
  };
};

/**
 * 检查点是否在节点中心区域（用于拖动）
 */
export const isPointInNodeCenter = (
  node: NodeSingular,
  point: Position
): boolean => {
  const pos = node.position();
  const bb = node.boundingBox({ includeLabels: false });

  // 中心区域：节点中心附近15%半径的圆形区域
  const centerRadius = Math.min(bb.w, bb.h) * 0.15;
  const dx = point.x - pos.x;
  const dy = point.y - pos.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  return distance <= centerRadius;
};

/**
 * 检查点是否在可拖动区域（文字区域或中心区域）
 */
export const isPointInDraggableArea = (
  node: NodeSingular,
  point: Position
): boolean => {
  return isPointInLabelArea(node, point) || isPointInNodeCenter(node, point);
};

/**
 * 检查点是否在节点边界内但不在可拖动区域内
 * 用于判断是否应该进入连线模式
 */
export const isPointInNodeButNotDraggable = (
  node: NodeSingular,
  point: Position
): boolean => {
  const bb = node.boundingBox({ includeLabels: false });
  const isInNode =
    point.x >= bb.x1 &&
    point.x <= bb.x2 &&
    point.y >= bb.y1 &&
    point.y <= bb.y2;

  if (!isInNode) return false;

  return !isPointInDraggableArea(node, point);
};

/**
 * 更新节点尺寸到 MMNode 模型
 * 对于父节点（compound node），需要同时更新 min-width/min-height 样式
 */
export const updateNodeSizeToModel = (
  node: NodeSingular,
  width: number,
  height: number
) => {
  // 同时更新 cytoscape 节点的数据
  node.data({
    customWidth: width,
    customHeight: height,
  });

  // 对于父节点，确保 min-width/min-height 也被正确设置
  // 这样在重新加载图表时能正确恢复尺寸
  if (node.isParent()) {
    node.style({
      'min-width': width,
      'min-height': height,
    });
  }

  // 清除该节点的边界框缓存
  clearLabelBBoxCache(node.id());
};

/**
 * 从 MMNode 模型恢复节点尺寸
 * 对于父节点（compound node），需要同时恢复 min-width/min-height 样式
 */
export const restoreNodeSizeFromModel = (node: NodeSingular) => {
  const customWidth = node.data('customWidth');
  const customHeight = node.data('customHeight');
  if (Number.isFinite(customWidth) && Number.isFinite(customHeight)) {
    node.data({
      customWidth,
      customHeight,
    });

    // 对于父节点，同时恢复 min-width/min-height 样式
    if (node.isParent()) {
      node.style({
        'min-width': customWidth,
        'min-height': customHeight,
      });
    }

    return true;
  }
  return false;
};

/**
 * 检查框选区域是否包含节点
 */
export const isNodeInBoxSelection = (
  node: NodeSingular,
  boxStart: Position,
  boxEnd: Position,
  wholeNode: boolean = false
): boolean => {
  const bb = node.boundingBox({ includeLabels: false });

  // 规范化框选区域坐标
  const x1 = Math.min(boxStart.x, boxEnd.x);
  const y1 = Math.min(boxStart.y, boxEnd.y);
  const x2 = Math.max(boxStart.x, boxEnd.x);
  const y2 = Math.max(boxStart.y, boxEnd.y);

  if (wholeNode) {
    // 整个节点都在框选区域内
    return bb.x1 >= x1 && bb.x2 <= x2 && bb.y1 >= y1 && bb.y2 <= y2;
  } else {
    // 节点与框选区域相交
    return !(bb.x2 < x1 || bb.x1 > x2 || bb.y2 < y1 || bb.y1 > y2);
  }
};

/**
 * 获取框选区域内的所有节点
 */
export const getNodesInBoxSelection = (
  cy: Core,
  boxStart: Position,
  boxEnd: Position,
  wholeNode: boolean = false
): NodeSingular[] => {
  const nodes: NodeSingular[] = [];

  cy.nodes().forEach((node: NodeSingular) => {
    // 跳过隐藏的节点
    if (node.data('display') === 'none') return;
    // 跳过连线小标节点
    if (node.data('isMarkerLabel')) return;

    if (isNodeInBoxSelection(node, boxStart, boxEnd, wholeNode)) {
      nodes.push(node);
    }
  });

  return nodes;
};
